import Rails from "@rails/ujs"
import { Controller } from "@hotwired/stimulus"

import loadStripeScript from "../app/components/stripe-script-loader"

export default class extends Controller {
  static values = {
    currency: { type: String, default: 'usd' },
    mode: { type: String, default: 'payment' },
    accountId: String,
    accountName: String,
    total: Number
  }

  static targets = [ "cardElement", "addressElement",
                     "cardErrors", "loadErrors"]

  initialize() {
    // needed before init() so __loadError() can still disable the form
    // when the Stripe script never arrives and init() is never called
    this.$form = $(this.element)
    this.isSubmitting = false

    loadStripeScript().
      then(() => this.init()).
      catch(() => this.__loadError())
  }

  connect() {
    // an attempt rejected by our server (see pay.js.erb) releases the lock so the customer can retry.
    // deliberately not payment:idle - we dispatch that one ourselves on widget updates
    // and must not let it unlock a submit that is still in flight
    this.releaseSubmitLock = () => {
      this.isSubmitting = false
      this.readyToSubmit = false
    }

    document.addEventListener('payment:rejected', this.releaseSubmitLock)
  }

  disconnect() {
    document.removeEventListener('payment:rejected', this.releaseSubmitLock)
  }

  init() {
    try {
      // TODO: implement later, collecting personal data to create a customer on CB side
      // this.customerName = ""

      this.stripe = Stripe(this.data.get('key'))

      let options = {
        currency: this.currencyValue.toLowerCase(),
        onBehalfOf: this.accountIdValue,
        appearance: {
          theme: 'stripe',
          variables: {
            colorTextPlaceholder: '#B3BCC7',
            colorText: '#071E3E'
          }
        }
      }

      if( this.modeValue == 'save_card' ) {
        options.mode = 'setup'
        options.setupFutureUsage = 'off_session'
      } else {
        options.mode = 'payment'
        options.amount = this.totalValue
      }

      this.stripeElements = this.stripe.elements(options)

      let paymentOptions = {
        paymentMethodOrder: ['card', 'apple_pay', 'google_pay']
      }

      if( this.hasAccountNameValue ) {
        paymentOptions.business = {
          name: this.accountNameValue
        }
      }

      this.card = this.stripeElements.create('payment', paymentOptions)
      this.card.mount(this.cardElementTarget)

      // a mounted element is not a usable one, only "ready" means the widget can take input
      this.card.on('ready', () => this.__paymentReady())

      if(this.hasAddressElementTarget) {
        let addressOptions = { mode: 'billing' }
        this.address = this.stripeElements.create('address', addressOptions)
        this.address.mount(this.addressElementTarget)
      } else {
        this.address = null
      }

      // automatically enable pay buttons after stripe widget updates
      this.stripeElements.on('update-end', () => this.__enablePayButton())

      // TODO: implement later, collecting personal data to create a customer on CB side
      // if this.address (not nil)
      // this.address.on("change", (event) => {
      //   // console.log("changing")
      //   // console.log(event.value)
      //   this.customerName = event.value.name
      // })

      this.$form.on("form:readyToSubmit", (event) => {
        this.readyToSubmit = true
        this.internalSubmit = true
        this.$form.submit()
      })

      this.$form.submit((event) => {
        // our own re-submit after the confirmation token was attached, let it through
        if(this.internalSubmit) {
          this.internalSubmit = false
        } else if(this.isSubmitting) {
          // returning false here stops propagation, so rails-ujs never sees this submit
          // and data-disable-with can't help us, the lock is the only guard
          return false
        }

        if(!this.readyToSubmit && $(this.cardElementTarget).is(':visible')) {
          this.isSubmitting = true
          this.__requestStripeConfirmationToken()
          return false // previous method will initialize new submit process to re-read inputs so interrupt this one to avoid double submitting
        } else if(this.$form.find("[name='paylink_payment_form[card_choice]']").is(':visible') &&
                  this.$form.find("[name='paylink_payment_form[card_choice]']:checked").val() == 'saved' ) {
          this.readyToSubmit = true
        }

        if(this.readyToSubmit) this.isSubmitting = true

        return this.readyToSubmit
      })

    } catch(e) {
      this.__loadError()
    }
  }

  __requestStripeConfirmationToken() {
    if($(this.cardElementTarget).is(':visible')) {
      this.__disablePayButton()

      this.stripeElements.submit().then((result) => { // elements validation before requesting confirmation token
        if(result.error) {
          this.__attemptFailed()
        } else {
          this.stripe.createConfirmationToken({elements: this.stripeElements}).then((result) => {
            this.__errorsHandler(result)

            if(result.error) {
              this.__attemptFailed()
            } else {
              // Send the token to server
              this.readyToSubmit = true
              this.__stripeTokenHandler(result.confirmationToken)
            }
          })
        }
      })
    }
  }

  toggleSaveCard(event) {
    this.__disablePayButton()

    let setupFutureUsage = "on_session"
    let mode = "payment"

    // if we need to save card
    if(event.target.checked) {
      setupFutureUsage = "off_session"
      mode = "setup"
    }

    this.stripeElements.update({
      mode: mode,
      setupFutureUsage: setupFutureUsage
    })
  }

  __paymentReady() {
    this.dispatch('ready', { prefix: 'payment', target: document })
  }

  // the attempt is over and the customer may try again
  __attemptFailed() {
    this.readyToSubmit = false
    this.isSubmitting = false
    this.__enablePayButton()
  }

  __disablePayButton() {
    this.dispatch('busy', { prefix: 'payment', target: document })
  }

  __enablePayButton() {
    this.dispatch('idle', { prefix: 'payment', target: document })
  }

  // Handle real-time validation errors from the stripe elements
  __errorsHandler(event) {
    if(event.error) {
      this.cardErrorsTarget.innerText = event.error.message
      this.readyToSubmit = false
    } else {
      this.cardErrorsTarget.innerText = ''
    }
  }

  // Submit the form with the token ID.
  __stripeTokenHandler(token) {
    let hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', 'stripe_token')
    hiddenInput.setAttribute('value', token.id)

    this.$form.append(hiddenInput)

    // resubmit form once more to re-construct request params with new input
    // if the form "remote" submit it with the Rails default handler
    // otherwise use regular submit method
    if(this.$form.data("remote")) {
      this.internalSubmit = true
      Rails.fire(this.$form.get(0), 'submit')
    } else {
      // native submit doesn't run our handler, so no need to mark it as internal
      this.$form.get(0).submit()
    }
  }

  __loadError() {
    if(this.hasLoadErrorsTarget) $(this.loadErrorsTarget).removeClass('d-none')
    this.$form.submit(() => false)
    this.dispatch('failed', { prefix: 'payment', target: document })
  }
}
